import React from "react";
import { Link, Navigate } from "react-router-dom";
import { CalendarPlusIcon, ClockIcon, MapPinIcon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useBookings } from "../contexts/BookingsContext";
import { AppointmentRow } from "../components/account/AppointmentRow";
import { CancelAppointment } from "../components/account/CancelAppointment";
import { clinic } from "../data/clinic";
import { services } from "../data/services";
import { parseDateKey, toDateTime } from "../utils/slots";
import { formatRelativeDay, formatTime } from "../utils/format";
export function Account() {
  const {
    user,
    logout
  } = useAuth();
  const {
    appointments
  } = useBookings();
  if (!user) return <Navigate to="/connexion" state={{
    from: "/mon-espace"
  }} replace data-id="element-330" data-mp="1uh2r5pl1mon9" />;
  const now = Date.now();
  const upcoming = appointments.filter(a => a.status === "confirmed" && toDateTime(a.dateKey, a.time).getTime() > now);
  const history = appointments.filter(a => !upcoming.includes(a)).reverse();
  const next = upcoming[0];
  const nextService = next ? services.find(s => s.id === next.serviceId) : undefined;
  return <div className="bg-surface" data-id="element-331" data-mp="31xe2cskv8dzs">
      <div className="mx-auto max-w-6xl px-5 py-10 md:px-8 md:py-14" data-id="element-332" data-mp="2x42uco4bhr6a">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between" data-id="element-333" data-mp="2x287v5dedr03">
          <h1 className="text-3xl font-semibold tracking-tight text-ink md:text-4xl" data-id="element-334" data-mp="2t0wqfn4ogc2z">Bonjour {user.firstName}</h1>
          <Link to="/reservation" className="inline-flex items-center justify-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-medium text-white transition-colors duration-150 hover:bg-brand-dark" data-id="element-335" data-mp="2j4tejpk0wz2u">
            <CalendarPlusIcon className="h-4 w-4" aria-hidden="true" data-id="element-336" data-mp="2a56mhud6p5mg" />
            Nouveau rendez-vous
          </Link>
        </div>

        <div className="mt-8 grid items-start gap-6 lg:grid-cols-12 lg:gap-8" data-id="element-337" data-mp="cdkfru32og4y">
          <div className="space-y-6 lg:col-span-8" data-id="element-338" data-mp="8e38qw7zzaxo">
            {next && nextService ? <section aria-labelledby="next-title" className="rounded-3xl bg-brand p-6 text-white md:p-10" data-id="element-339" data-mp="21upiiquczyrt">
                <h2 id="next-title" className="text-sm text-white/75" data-id="element-340" data-mp="26nsef5mlzkb6">
                  Prochain rendez-vous
                </h2>
                <p className="mt-3 text-3xl font-semibold tracking-tight md:text-4xl" data-id="element-341" data-mp="1sszs9oc7r5vm">
                  {formatRelativeDay(parseDateKey(next.dateKey))}
                </p>
                <p className="mt-1 text-xl text-white/90" data-id="element-342" data-mp="2kmiemu4q603r">à {formatTime(next.time)}</p>
                <div className="mt-6 flex flex-col gap-2 text-sm text-white/80 sm:flex-row sm:gap-6" data-id="element-343" data-mp="2pjsbuvycvg25">
                  <span className="inline-flex items-center gap-2" data-id="element-344" data-mp="2bkskvhndep2t">
                    <ClockIcon className="h-4 w-4" aria-hidden="true" data-id="element-345" data-mp="2gjlzxeib2o53" />
                    {nextService.name} · {nextService.duration} min
                  </span>
                  <span className="inline-flex items-center gap-2" data-id="element-346" data-mp="108hzvxevbx8x">
                    <MapPinIcon className="h-4 w-4" aria-hidden="true" data-id="element-347" data-mp="156t9q855y7r5" />
                    {clinic.street}, Étréchy
                  </span>
                </div>
                <div className="mt-8 border-t border-white/15 pt-6" data-id="element-348" data-mp="sne4emjcu0e7">
                  <CancelAppointment appointment={next} onDark data-id="element-349" data-mp="16mdsnt2ygd9u" />
                </div>
              </section> : <section className="rounded-3xl border border-line bg-white p-8 text-center md:p-12" data-id="element-350" data-mp="11msqzawehqtf">
                <h2 className="text-xl font-semibold text-ink" data-id="element-351" data-mp="dwyif5j46oez">Aucun rendez-vous à venir</h2>
                <p className="mt-2 text-sm text-muted" data-id="element-352" data-mp="8z5c8cht9uep">Réservez votre prochaine séance en moins d'une minute.</p>
                <Link to="/reservation" className="mt-6 inline-flex rounded-full bg-brand px-6 py-3 text-sm font-medium text-white transition-colors duration-150 hover:bg-brand-dark" data-id="element-353" data-mp="nnt8kkzhe85p">
                  Prendre rendez-vous
                </Link>
              </section>}

            {upcoming.length > 1 && <section className="rounded-3xl border border-line bg-white px-6 pt-6 md:px-8" data-id="element-354" data-mp="2pmeqqokaie0q">
                <h2 className="text-sm font-semibold text-ink" data-id="element-355" data-mp="2ukzrcp3gezpp">Également à venir</h2>
                <ul className="divide-y divide-line" data-id="element-356" data-mp="2uivaz7odc88j">
                  {upcoming.slice(1).map(a => <AppointmentRow key={a.id} appointment={a} variant="upcoming" data-id="element-357" data-mp="33djil4hf220i" />)}
                </ul>
              </section>}

            {history.length > 0 && <section className="rounded-3xl border border-line bg-white px-6 pt-6 md:px-8" data-id="element-358" data-mp="3dbqv9b4o9xwp">
                <h2 className="text-sm font-semibold text-ink" data-id="element-359" data-mp="3iau1v00awc6s">Historique</h2>
                <ul className="divide-y divide-line" data-id="element-360" data-mp="3i8pozskfc2ud">
                  {history.map(a => <AppointmentRow key={a.id} appointment={a} variant="history" data-id="element-361" data-mp="3mbm9xpxfsb1h" />)}
                </ul>
              </section>}
          </div>

          <aside className="space-y-6 lg:col-span-4" data-id="element-362" data-mp="1rz5zecxlyaqt">
            <section className="rounded-3xl border border-line bg-white p-6" data-id="element-363" data-mp="1kelkofg0cw8j">
              <h2 className="text-sm font-semibold text-ink" data-id="element-364" data-mp="1kgpypq7mfbmj">Mes informations</h2>
              <dl className="mt-4 space-y-3 text-sm" data-id="element-365" data-mp="1fmu5f12aycxp">
                <div data-id="element-366" data-mp="1aoagcrsewl14">
                  <dt className="text-xs text-muted" data-id="element-367" data-mp="1apvcumyaee1m">Nom</dt>
                  <dd className="text-ink" data-id="element-368" data-mp="16nqkrsnhlph3">
                    {user.firstName} {user.lastName}
                  </dd>
                </div>
                <div data-id="element-369" data-mp="23cfyr7vztxxs">
                  <dt className="text-xs text-muted" data-id="element-370" data-mp="23bn2x1ykosuf">E-mail</dt>
                  <dd className="break-all text-ink" data-id="element-371" data-mp="1yfpo502yjujr">{user.email}</dd>
                </div>
                <div data-id="element-372" data-mp="1uc9pjy7bvk4f">
                  <dt className="text-xs text-muted" data-id="element-373" data-mp="1ubhucohn7oeo">Téléphone</dt>
                  <dd className="text-ink" data-id="element-374" data-mp="1perg86pef2vw">{user.phone}</dd>
                </div>
              </dl>
              <button type="button" onClick={logout} className="mt-6 w-full rounded-full border border-line py-2.5 text-sm font-medium text-ink transition-colors duration-150 hover:border-brand hover:text-brand" data-id="element-375" data-mp="iy6095boqk5p">
                Se déconnecter
              </button>
            </section>

            <section className="rounded-3xl bg-white p-6" data-id="element-376" data-mp="kcywk4xuy2a9">
              <h2 className="text-sm font-semibold text-ink" data-id="element-377" data-mp="6e8qhzt28s9r">Besoin d'aide ?</h2>
              <p className="mt-2 text-sm leading-relaxed text-muted" data-id="element-378" data-mp="agcbpg5j7b88">
                Annulation en ligne jusqu'à 24 h avant. Au-delà, ou pour toute question, contactez le cabinet.
              </p>
              <a href={clinic.phoneHref} className="mt-3 inline-block font-semibold text-brand hover:underline" data-id="element-379" data-mp="1gpdf383mlje">
                {clinic.phone}
              </a>
            </section>
          </aside>
        </div>
      </div>
    </div>;
}